<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
    @keydown.esc="dialog = false"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn color="primary" dark v-bind="attrs" v-on="on" @click="reset">
        Add Event
      </v-btn>
    </template>
    <v-card>
      <v-card-title>
        <span class="text-h5">Add Event</span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12">
              <v-btn-toggle v-model="type" tile color="primary accent-3" group>
                <v-btn value="goal" @click="reset">
                  GOAL
                </v-btn>
                <v-btn value="penalty" @click="reset">
                  PENALTY GOAL
                </v-btn>
                <v-btn value="penaltyMiss" @click="reset">
                  PENALTY MISS
                </v-btn>
                <v-btn value="ownGoal" @click="reset">
                  OWN GOAL
                </v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-btn
                v-for="player in playersCopy"
                :key="player.nickname"
                :color="player.colour"
                small
                class="ma-2"
                @click="moveToNextEmptyDragDropArea(player)"
              >
                {{ player.nickname }}
              </v-btn>
            </v-col>
          </v-row>
          <template v-if="type === 'goal'">
            <v-row>
              <v-col cols="6">
                <div class="dragDropArea">
                  <drag-drop-area
                    friendly-desc="goal"
                    :player="arrGoal[0]"
                    array-name="arrGoal" />
                </div>
              </v-col>
              <v-col cols="6">
                <div class="dragDropArea">
                  <drag-drop-area
                    friendly-desc="assist"
                    :player="arrAssist[0]"
                    array-name="arrAssist"
                  />
                </div>
              </v-col>
            </v-row>
          </template>
          <template v-else-if="type === 'penalty'">
            <v-row>
              <v-col cols="6">
                <div class="dragDropArea">
                  <drag-drop-area
                    friendly-desc="conceded by"
                    :player="arrPenaltyGoalConcededBy[0]"
                    array-name="arrPenaltyGoalConcededBy"
                  />
                </div>
              </v-col>
              <v-col cols="6">
                <div class="dragDropArea">
                  <drag-drop-area
                    friendly-desc="penalty scored by"
                    :player="arrPenaltyGoalBy[0]"
                    array-name="arrPenaltyGoalBy"
                  />
                </div>
              </v-col>
            </v-row>
          </template>
          <template v-else-if="type === 'penaltyMiss'">
            <v-row>
              <v-col cols="4">
                <div class="dragDropArea">
                  <drag-drop-area
                    friendly-desc="conceded by"
                    :player="arrPenaltyMissConcededBy[0]"
                    array-name="arrPenaltyMissConcededBy"
                  />
                </div>
              </v-col>
              <v-col cols="4">
                <div class="dragDropArea">
                  <drag-drop-area
                    friendly-desc="missed by"
                    :player="arrPenaltyMissMissBy[0]"
                    array-name="arrPenaltyMissMissBy"
                  />
                </div>
              </v-col>
              <v-col cols="4">
                <div class="dragDropArea">
                  <drag-drop-area
                    friendly-desc="saved by"
                    :player="arrPenaltyMissSavedBy[0]"
                    array-name="arrPenaltyMissSavedBy"
                  />
                </div>
              </v-col>
            </v-row>
          </template>
          <template v-else>
            <v-row>
              <v-col cols="6">
                <div class="dragDropArea">
                  <drag-drop-area
                    friendly-desc="own goal"
                    :player="arrOwnGoal[0]"
                    array-name="arrOwnGoal"
                  />
                </div>
              </v-col>
              <v-col cols="6">
                <div class="dragDropArea">
                  <drag-drop-area
                    friendly-desc="assist"
                    :player="arrOwnGoalAssist[0]"
                    array-name="arrOwnGoalAssist"
                  />
                </div>
              </v-col>
            </v-row>
          </template>
          <v-row>
            <v-col cols="4">
              <v-switch
                v-model="footage"
                :label="`${footage ? 'Video available' : 'No video'}`"
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                ref="min"
                v-model="min.val"
                label="Mins"
                type="number"
                :rules="minRules"
                :disabled="!footage"
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="secs.val"
                label="Seconds"
                type="number"
                :rules="secRules"
                :disabled="!footage"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-switch
                v-model="approved"
                :label="`${approved ? 'Event approved' : 'Requires approval'}`"
              />
            </v-col>
          </v-row>
          <v-row v-show="false">
            <v-col>
              <v-slider
                v-model="min.val"
                :label="min.label"
                :thumb-color="min.color"
                thumb-label="always"
                :min="0"
                :max="100"
              />
            </v-col>
            <v-col cols="6">
              <v-slider
                v-model="secs.val"
                :label="secs.label"
                :thumb-color="secs.color"
                thumb-label="always"
                :min="0"
                :max="59"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="blue darken-1" text @click="dialog = false">
          Close
        </v-btn>
        <v-btn color="blue darken-1" text @click="saveEvent()">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import DragDropArea from './DragDropArea.vue';
import { addMinutes, addSeconds, intervalToDuration } from 'date-fns';
export default {
  name: 'AddPointRegUser',
  components: {
    DragDropArea,
  },
  props: {
    players: Array,
  },
  data: () => ({
    addMinutes,
    addSeconds,
    intervalToDuration,
    dialog: false,
    type: 'goal',
    scorer: null,
    assist: null,
    min: { label: 'Mins', val: 0, color: 'red' },
    secs: { label: 'Secs', val: 0, color: 'blue' },
    minRules: [
      v => !!v || 'This field is required',
      v => (v && v >= 0) || 'Mins have to be 0 or more',
      v => (v && v <= 100) || 'Should not be above 100*',
    ],
    secRules: [
      v => !!v || 'This field is required',
      v => (v && v >= 0) || 'Secs have to be 0 or more',
      v => (v && v <= 59) || 'Should be less than 60',
    ],
    playersCopy: [],
    arrGoal: [],
    arrAssist: [],
    arrPenaltyGoalConcededBy: [],
    arrPenaltyGoalBy: [],
    arrPenaltyMissConcededBy: [],
    arrPenaltyMissMissBy: [],
    arrPenaltyMissSavedBy: [],
    arrOwnGoal: [],
    arrOwnGoalAssist: [],
    dtMatchStartDateTime: null,
    dtLater: null,
    footage: true,
    approved: false,
  }),
  computed: {
    ...mapState('leagues', ['match']),
    ...mapState('auth', ['user']),
  },
  watch: {
    arrPenaltyMissConcededBy: function() {
      const players = new Set(this.players);
      this.playersCopy = [...players];
    },
    arrPenaltyMissSavedBy: function() {
      const players = new Set(this.players);
      this.playersCopy = [...players];
    },
  },
  mounted() {
    this.reset();
    this.approved = this.user.email === 'rahulvaghela@hotmail.com'
    this.$eventBus.$on('remove-player-selected', (arrayName) => {
      // clear the array
      this[arrayName] = [];
    })
  },
  methods: {
    ...mapActions('leagues', ['addEvent', 'getMatchPoints']),
    async saveEvent() {
      const matchId = this.match._id;
      const min = this.footage ? parseInt(this.min.val) : -1;
      const sec = this.footage ? parseInt(this.secs.val) : -1;

      const payload = {
        matchId,
        type: this.type,
        min: min,
        sec: sec,
        approved: this.approved,
        submittedBy: this.user.nickname,
        submittedById: this.user._id,
        category: 'individual',
      };

      if (this.type === 'goal') {
        payload.goalBy = this.arrGoal[0];
        payload.assistBy = this.arrAssist[0];
      } else if (this.type === 'ownGoal') {
        payload.ownGoalBy = this.arrOwnGoal[0];
        payload.ownGoalAssistBy = this.arrOwnGoalAssist[0];
      } else if (this.type === 'penalty') {
        payload.penaltyGoalBy = this.arrPenaltyGoalBy[0];
        payload.penaltyConcededBy = this.arrPenaltyGoalConcededBy[0];
      } else if (this.type === 'penaltyMiss') {
        payload.penaltyMissBy = this.arrPenaltyMissMissBy[0];
        payload.penaltySavedBy = this.arrPenaltyMissSavedBy[0];
        payload.penaltyConcededBy = this.arrPenaltyMissConcededBy[0];
      }

      await this.addEvent(payload);

      this.dialog = false;
    },
    reset() {
      const players = new Set(this.players);
      this.playersCopy = [...players];
      this.arrGoal = [];
      this.arrAssist = [];
      this.arrPenaltyGoalConcededBy = [];
      this.arrPenaltyGoalBy = [];
      this.arrPenaltyMissConcededBy = [];
      this.arrPenaltyMissMissBy = [];
      this.arrPenaltyMissSavedBy = [];
      this.arrOwnGoal = [];
      this.arrOwnGoalAssist = [];
    },
    bgColor(color) {
      switch (color) {
        case 'orange':
          return 'orange';
        case 'blue':
          return '#2196F3';
        default:
          return 'black';
      }
    },
    moveToNextEmptyDragDropArea(player) {
      if (this.type === 'goal') {
        if (this.arrGoal.length === 0) {
          this.arrGoal.push(player)
        } else if (this.arrAssist.length === 0) {
          this.arrAssist.push(player)
        }
      } else if (this.type === 'penalty') {
        if (this.arrPenaltyGoalConcededBy.length === 0) {
          this.arrPenaltyGoalConcededBy.push(player)
        } else if (this.arrPenaltyGoalBy.length === 0) {
          this.arrPenaltyGoalBy.push(player)
        }
      } else if (this.type === 'penaltyMiss') {
        if (this.arrPenaltyMissConcededBy.length === 0) {
          this.arrPenaltyMissConcededBy.push(player)
        } else if (this.arrPenaltyMissMissBy.length === 0) {
          this.arrPenaltyMissMissBy.push(player)
        } else if (this.arrPenaltyMissSavedBy.length === 0) {
          this.arrPenaltyMissSavedBy.push(player)
        }
      } else if (this.type === 'ownGoal') {
        if (this.arrOwnGoal.length === 0) {
          this.arrOwnGoal.push(player)
        } else if (this.arrOwnGoalAssist.length === 0) {
          this.arrOwnGoalAssist.push(player)
        }
      }
    },
  },
};
</script>

<style scoped>
.dragDropArea {
  width: 100%;
  height: 44px;
  border: 1px dashed grey;
  border-radius: 5px;
  line-height: 48px;
  text-align: center;
  color: #333333;
}
.dragDropAreaText {
  font-weight: bold;
  height: 100%;
}
</style>
